/**
 * LLMProvidersPage / LLMProvidersSection — gestión per-org de credenciales LLM (TKT-97).
 *
 * Cada org configura SUS PROPIAS API keys de Anthropic/OpenAI/Google/etc.
 * Cliente paga sus tokens, no la balance global de Tot BI.
 *
 * Diseño visual: alineado con `CompanyThirdPartyKeys` del mismo dashboard
 * (o-card + var(--acc1)/var(--acc2) + SecretField-style inputs). No usar
 * tailwind genérico tipo bg-slate-* — rompe el theme.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Key,
  Plus,
  Save,
  Trash2,
  TestTube2,
  CheckCircle2,
  AlertCircle,
  Loader,
  X,
  Eye,
  EyeOff,
  Sparkles,
} from 'lucide-react';

import {
  listLLMProviders,
  listLLMProviderChoices,
  createLLMProvider,
  updateLLMProvider,
  deleteLLMProvider,
  testLLMProvider,
} from '@/lib/api';
import type {
  OrgLLMProvider,
  LLMProviderChoice,
  TestConnectionResult,
} from '@/lib/api';

interface EditorState {
  mode: 'create' | 'edit';
  row: OrgLLMProvider | null;
  provider: string;
  api_key: string;
  default_model: string;
  is_active: boolean;
  notes: string;
}

function emptyEditor(): EditorState {
  return {
    mode: 'create',
    row: null,
    provider: 'anthropic',
    api_key: '',
    default_model: '',
    is_active: true,
    notes: '',
  };
}

/**
 * Variante inline (sin wrapper full-page). Diseñada para embeber dentro
 * de Empresa → API keys, debajo del bloque 'API keys de terceros'.
 */
export function LLMProvidersSection() {
  return <LLMProvidersBody />;
}

/** Página completa (legacy, no usada en routing actual). */
export function LLMProvidersPage() {
  return (
    <div className="max-w-xl space-y-4">
      <LLMProvidersBody />
    </div>
  );
}

function LLMProvidersBody() {
  const [providers, setProviders] = useState<OrgLLMProvider[]>([]);
  const [choices, setChoices] = useState<LLMProviderChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, opts] = await Promise.all([
        listLLMProviders(),
        listLLMProviderChoices(),
      ]);
      setProviders(rows);
      setChoices(opts);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditor({ ...emptyEditor(), provider: choices[0]?.value || 'anthropic' });
    setShowApiKey(false);
  };

  const openEdit = (row: OrgLLMProvider) => {
    setEditor({
      mode: 'edit',
      row,
      provider: row.provider,
      api_key: '',
      default_model: row.default_model,
      is_active: row.is_active,
      notes: row.notes,
    });
    setShowApiKey(false);
  };

  const close = () => {
    setEditor(null);
    setTestResult(null);
  };

  const save = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      if (editor.mode === 'create') {
        if (!editor.api_key.trim()) {
          setError('API key requerida para nuevo proveedor');
          return;
        }
        await createLLMProvider({
          provider: editor.provider,
          api_key: editor.api_key,
          default_model: editor.default_model,
          is_active: editor.is_active,
          notes: editor.notes,
        });
      } else if (editor.row) {
        await updateLLMProvider(editor.row.id, {
          api_key: editor.api_key.trim() || undefined,
          default_model: editor.default_model,
          is_active: editor.is_active,
          notes: editor.notes,
        });
      }
      close();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: OrgLLMProvider) => {
    if (!confirm(`Borrar credenciales de ${row.provider_display}? Las llamadas LLM de esta org caerán al fallback global del backend.`)) {
      return;
    }
    try {
      await deleteLLMProvider(row.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const test = async (row: OrgLLMProvider) => {
    setTestingId(row.id);
    setTestResult(null);
    try {
      const result = await testLLMProvider(row.id);
      setTestResult(result);
      await load();
    } catch (e) {
      setTestResult({
        ok: false,
        provider: row.provider,
        model_used: '',
        latency_ms: 0,
        error: e instanceof Error ? e.message : String(e),
        tested_at: new Date().toISOString(),
      });
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="o-card space-y-4">
      {/* Header — replica el patrón visual del bloque "API keys de terceros" */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Key size={18} style={{ color: 'var(--acc2)' }} />
            <h2 className="text-lg font-bold text-white">Proveedores LLM</h2>
          </div>
          <p className="text-white/40 text-xs -mt-1">
            Configura las API keys de tu org para Claude, OpenAI, Gemini y otros.
            Cada llamada de IA hecha por tu agente usará tus tokens. Si no
            configuras ninguno, el sistema cae al proveedor global del backend.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="o-btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-xs whitespace-nowrap"
          style={{ color: 'var(--acc1)' }}
        >
          <Plus size={14} /> Añadir proveedor
        </button>
      </div>

      {/* Error global */}
      {error && (
        <div
          className="rounded-lg p-3 flex items-start gap-2 text-xs"
          style={{
            background: 'rgba(var(--urgent-rgb, 239 68 68), .12)',
            border: '1px solid rgba(var(--urgent-rgb, 239 68 68), .25)',
            color: 'var(--urgent, #ef4444)',
          }}
        >
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError(null)} className="opacity-60 hover:opacity-100">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-6 text-white/30 text-xs gap-2">
          <Loader size={14} className="animate-spin" /> Cargando proveedores…
        </div>
      ) : providers.length === 0 ? (
        <div className="rounded-lg p-6 text-center bg-white/[.02] border border-white/5">
          <Sparkles size={24} className="mx-auto text-white/20 mb-2" />
          <p className="text-white/60 text-sm mb-1">
            Sin proveedores LLM configurados todavía.
          </p>
          <p className="text-white/35 text-xs">
            Por ahora tu org usa el proveedor global del backend. Añade el tuyo
            para tener control de costos, modelo y trazabilidad.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {providers.map((p) => (
            <ProviderRow
              key={p.id}
              row={p}
              testing={testingId === p.id}
              onEdit={() => openEdit(p)}
              onDelete={() => void remove(p)}
              onTest={() => void test(p)}
            />
          ))}
        </div>
      )}

      <p className="text-[10px] text-white/30">
        💡 Cuando una credencial está configurada aquí, el backend la usa en lugar del proveedor global.
        La api_key se guarda encriptada y nunca se muestra en texto plano — para cambiarla, re-pégala en el editor.
      </p>

      {/* Toast resultado test */}
      {testResult && (
        <div
          className="fixed bottom-6 right-6 max-w-md p-3 rounded-lg shadow-xl text-xs z-50"
          style={{
            background: testResult.ok
              ? 'linear-gradient(180deg, rgba(34,197,94,.18), rgba(34,197,94,.10))'
              : 'linear-gradient(180deg, rgba(239,68,68,.18), rgba(239,68,68,.10))',
            border: `1px solid ${testResult.ok ? 'rgba(34,197,94,.35)' : 'rgba(239,68,68,.35)'}`,
            color: testResult.ok ? '#bbf7d0' : '#fecaca',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div className="flex items-start gap-2">
            {testResult.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            <div className="flex-1">
              <div className="font-medium">
                {testResult.ok ? 'Conexión OK' : 'Conexión falló'} · {testResult.provider}
              </div>
              {testResult.model_used && (
                <div className="opacity-80 mt-0.5">
                  modelo: <code>{testResult.model_used}</code> · {testResult.latency_ms}ms
                </div>
              )}
              {testResult.error && (
                <div className="opacity-90 mt-1 font-mono break-all">{testResult.error}</div>
              )}
            </div>
            <button onClick={() => setTestResult(null)} className="opacity-70 hover:opacity-100">
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Modal editor */}
      {editor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="o-card w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white">
                {editor.mode === 'create' ? 'Añadir proveedor' : `Editar ${editor.row?.provider_display}`}
              </h3>
              <button onClick={close} className="text-white/40 hover:text-white">
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              {editor.mode === 'create' && (
                <div>
                  <label className="block text-white/40 uppercase tracking-wide mb-1 text-[10px]">
                    Proveedor
                  </label>
                  <select
                    value={editor.provider}
                    onChange={(e) => setEditor({ ...editor, provider: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md bg-white/5 border border-white/10 text-white outline-none focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)]"
                  >
                    {choices.map((c) => (
                      <option key={c.value} value={c.value} style={{ background: '#0a0a18' }}>
                        {c.label}
                        {c.has_global_fallback ? ' — sistema lo soporta' : ' — requiere tu key (sin fallback)'}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const selected = choices.find((c: LLMProviderChoice) => c.value === editor.provider);
                    if (!selected) return null;
                    return selected.has_global_fallback ? (
                      <p className="text-[10px] mt-1" style={{ color: 'var(--acc1)' }}>
                        ✓ Si no configuras key propia, el sistema cae al global del backend.
                      </p>
                    ) : (
                      <p className="text-[10px] mt-1 text-amber-300/80">
                        ⚠ El sistema NO tiene fallback global para este proveedor — sin tu key, las llamadas fallarán.
                      </p>
                    );
                  })()}
                </div>
              )}

              <div>
                <label className="block text-white/40 uppercase tracking-wide mb-1 text-[10px]">
                  API key {editor.mode === 'edit' && '(vacío = conservar la actual)'}
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={editor.api_key}
                    onChange={(e) => setEditor({ ...editor, api_key: e.target.value })}
                    placeholder={editor.mode === 'edit' ? '••••••••' : 'sk-ant-... / sk-... / AIza...'}
                    className="w-full px-3 py-2 pr-10 text-sm rounded-md bg-white/5 border border-white/10 text-white placeholder:text-white/20 outline-none focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)] font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  >
                    {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <p className="text-[10px] text-white/30 mt-1">
                  Se guarda encriptada. Nunca se muestra después de guardar.
                </p>
              </div>

              <div>
                <label className="block text-white/40 uppercase tracking-wide mb-1 text-[10px]">
                  Modelo default <span className="lowercase normal-case text-white/30">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={editor.default_model}
                  onChange={(e) => setEditor({ ...editor, default_model: e.target.value })}
                  placeholder={
                    choices.find((c: LLMProviderChoice) => c.value === editor.provider)?.default_model_hint
                    || 'modelo del proveedor'
                  }
                  className="w-full px-3 py-2 text-sm rounded-md bg-white/5 border border-white/10 text-white placeholder:text-white/20 outline-none focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)] font-mono"
                />
                <p className="text-[10px] text-white/30 mt-1">
                  Vacío = el sistema decide el modelo según el proveedor.
                </p>
              </div>

              <div>
                <label className="block text-white/40 uppercase tracking-wide mb-1 text-[10px]">
                  Notas internas
                </label>
                <textarea
                  value={editor.notes}
                  onChange={(e) => setEditor({ ...editor, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-md bg-white/5 border border-white/10 text-white placeholder:text-white/20 outline-none focus:ring-1 focus:ring-[var(--acc1)] focus:border-[var(--acc1)] resize-y"
                />
              </div>

              <label className="flex items-center gap-2 text-white/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editor.is_active}
                  onChange={(e) => setEditor({ ...editor, is_active: e.target.checked })}
                  className="rounded border-white/20"
                />
                <span className="text-xs">Activo (si está apagado, cae al proveedor global)</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
              <button onClick={close} className="o-btn-ghost px-3 py-2 text-xs">
                Cancelar
              </button>
              <button
                onClick={() => void save()}
                disabled={saving}
                className="o-btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-xs disabled:opacity-50"
                style={{ color: 'var(--acc1)' }}
              >
                {saving ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Una fila de proveedor configurado — replica el look de SecretField. */
function ProviderRow({
  row, testing, onEdit, onDelete, onTest,
}: {
  row: OrgLLMProvider;
  testing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onTest: () => void;
}) {
  return (
    <div className="rounded-lg p-3 bg-white/[.02] border border-white/10 hover:border-white/15 transition-colors">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">{row.provider_display}</span>
              {row.is_active ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: 'var(--acc1)', background: 'rgba(var(--acc1-rgb),.12)' }}>
                  activo
                </span>
              ) : (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/40">pausado</span>
              )}
              {row.last_test_status === 'ok' && (
                <span className="inline-flex items-center gap-1 text-[10px] text-green-400">
                  <CheckCircle2 size={10} /> ok
                </span>
              )}
              {row.last_test_status === 'failed' && (
                <span className="inline-flex items-center gap-1 text-[10px] text-red-400" title={row.last_test_error}>
                  <AlertCircle size={10} /> falló
                </span>
              )}
            </div>
            <div className="text-[10px] text-white/40 mt-0.5">
              {row.has_api_key ? '✓ api_key configurada' : '⚠ sin api_key'}
              {row.default_model && <span className="ml-2">· modelo: <code className="text-white/60">{row.default_model}</code></span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onTest}
            disabled={testing || !row.has_api_key}
            className="o-btn-ghost inline-flex items-center gap-1 px-2 py-1 text-[11px] disabled:opacity-40"
            title="Probar conexión live"
          >
            {testing ? <Loader size={11} className="animate-spin" /> : <TestTube2 size={11} />}
            Probar
          </button>
          <button onClick={onEdit} className="o-btn-ghost px-2 py-1 text-[11px]">
            Editar
          </button>
          <button
            onClick={onDelete}
            className="o-btn-ghost px-2 py-1 text-[11px] hover:text-red-300"
            title="Borrar"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}
